import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Users, MessagesSquare, Sparkles, Star, Crown, CalendarDays, Swords, Flame } from "lucide-react";
import { api } from "@/lib/api";

const SB_LABELS = {
  fr: { founders: "Les premiers Fondateurs", of: "sur", remaining: (n) => `plus que ${n} places`, satisfaction: "de satisfaction", conf: "Inscrits aux conférences", debates: "Participations aux débats", campfire: "Inscrits au feu de camp" },
  en: { founders: "The first Founders", of: "of", remaining: (n) => `only ${n} spots left`, satisfaction: "satisfaction", conf: "Conference sign-ups", debates: "Debate participations", campfire: "Campfire sign-ups" },
  es: { founders: "Los primeros Fundadores", of: "de", remaining: (n) => `solo quedan ${n} plazas`, satisfaction: "de satisfacción", conf: "Inscritos a conferencias", debates: "Participaciones en debates", campfire: "Inscritos al fuego de campamento" },
  nl: { founders: "De eerste Founders", of: "van", remaining: (n) => `nog ${n} plekken`, satisfaction: "tevredenheid", conf: "Conferentie-inschrijvingen", debates: "Debatdeelnames", campfire: "Kampvuur-inschrijvingen" },
};

function useCountUp(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || target == null) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

const fmt = (n) => new Intl.NumberFormat().format(n);

// Compact form (e.g. "12,4 k") above 10 000 so big serif numbers never overflow on mobile.
const fmtStat = (n, lang) =>
  n >= 10000
    ? new Intl.NumberFormat(lang, { notation: "compact", maximumFractionDigits: 1 }).format(n)
    : new Intl.NumberFormat(lang).format(n);

function Stat({ icon: Icon, value, label, active, testid, lang }) {
  const n = useCountUp(value, active);
  return (
    <div className="flex flex-col items-center text-center px-3" data-testid={testid}>
      <Icon size={18} className="text-primary/70 mb-2" strokeWidth={1.6} />
      <span className="font-display text-gradient-orange text-5xl sm:text-6xl leading-none tabular-nums">{fmtStat(n, lang)}</span>
      <span className="text-foreground/50 text-xs mt-2 max-w-[8rem] leading-snug">{label}</span>
    </div>
  );
}

export function StatsBand() {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const el = SB_LABELS[lang] || SB_LABELS.fr;
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const active = inView && !!stats;
  const fCurrent = useCountUp(stats?.founders_current, active);
  const goal = stats?.founders_goal || 500;
  const remaining = Math.max(0, goal - (stats?.founders_current || 0));
  const pct = stats ? Math.min(100, Math.round((stats.founders_current / goal) * 100)) : 0;
  const satis = (stats?.satisfaction ?? 4.9).toLocaleString(lang, { minimumFractionDigits: 1 });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      data-testid="stats-band"
      className="max-w-3xl mx-auto glass stats-wow rounded-2xl px-6 py-8"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 gap-x-2">
        <Stat icon={Users} value={stats?.explorers} label={t("landing.statsExplorers")} active={active} testid="stat-explorers" lang={lang} />
        <Stat icon={MessagesSquare} value={stats?.conversations} label={t("landing.statsConversations")} active={active} testid="stat-conversations" lang={lang} />
        <Stat icon={Sparkles} value={stats?.epiphanies} label={t("landing.statsEpiphanies")} active={active} testid="stat-epiphanies" lang={lang} />
        <Stat icon={Star} value={stats?.stars} label={t("landing.statsStars")} active={active} testid="stat-stars" lang={lang} />
      </div>

      {/* Inscriptions par domaine (conférence · débats · feu de camp) */}
      <div className="mt-8 pt-7 border-t border-primary/15 grid grid-cols-3 gap-x-2" data-testid="stats-domains">
        <Stat icon={CalendarDays} value={stats?.conf_registrations} label={el.conf} active={active} testid="stat-conf-registrations" lang={lang} />
        <Stat icon={Swords} value={stats?.debate_participation} label={el.debates} active={active} testid="stat-debate-participation" lang={lang} />
        <Stat icon={Flame} value={stats?.campfire_registrations} label={el.campfire} active={active} testid="stat-campfire-registrations" lang={lang} />
      </div>

      {/* Jauge « Fondateurs » (rareté) */}
      <div className="mt-8 pt-7 border-t border-primary/15" data-testid="founders-gauge">
        <div className="flex items-center justify-between mb-2.5 flex-wrap gap-y-1">
          <span className="inline-flex items-center gap-2 text-sm text-foreground/80">
            <Crown size={15} className="text-primary" /> {el.founders}
          </span>
          <span className="text-sm text-foreground/60 tabular-nums">
            <span className="font-display text-gradient-orange text-xl">{fmt(fCurrent)}</span>
            <span className="mx-1.5">{el.of}</span>
            <span>{fmt(goal)}</span>
          </span>
        </div>
        <div className="h-2.5 rounded-full bg-white/8 overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, hsl(28 90% 45%), hsl(38 95% 60%))" }}
            initial={{ width: 0 }}
            animate={{ width: active ? `${pct}%` : 0 }}
            transition={{ duration: 1.4, ease: "easeOut", delay: 0.2 }}
          />
        </div>
        <div className="flex items-center justify-between mt-2.5 flex-wrap gap-y-1">
          <span className="text-xs text-primary/80 font-medium" data-testid="founders-remaining">{el.remaining(remaining)}</span>
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground/55" data-testid="satisfaction-stat">
            <Star size={12} className="text-primary fill-primary/80" />
            <span className="font-medium text-foreground/80">{satis}/5</span> {el.satisfaction}
          </span>
        </div>
      </div>
    </motion.div>
  );
}
